import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { TransactionType } from '@/lib/api/types';
import type { BudgetTreatment } from '@/lib/constants';
import { BUDGET_MORE_TREATMENTS, BUDGET_UNASSIGNED_LABEL, translateBudgetTreatment } from '@/lib/translations';
import { cn } from '@/lib/utils';
import { treatmentsForCategoryType, type AssignmentPick } from '@/lib/utils/budget';

export interface TreatmentPillsProps {
  categoryType: TransactionType;
  // '' selects nothing — for a classification the client can't read.
  value: AssignmentPick | '';
  onPick: (pick: AssignmentPick) => void;
  // Names the group for screen readers ("Classificação de Mercado").
  label: string;
  disabled?: boolean;
}

// What most categories are: money in, money out, or neither. The rest — a refund, an earmark, a deduction — are
// real but rare, so they wait behind "Mais opções" instead of doubling every row's choices on day one.
const COMMON_TREATMENTS: ReadonlySet<BudgetTreatment> = new Set(['income', 'spend', 'excluded']);

// The dense variant of the toggle pill (DESIGN.md): one group per category row, so unselected pills are quiet
// text and the selected one takes a neutral tint. The shadcn toggle selects with the pale amber tint, which
// on a list of classified categories would paint one amber patch per row. 44px tall on touch, 36px from sm.
const PILL_CLASSES =
  'h-11 px-3 text-sm text-muted-foreground hover:bg-muted hover:text-foreground data-[state=on]:bg-foreground/10 data-[state=on]:text-foreground sm:h-9';
// "Sem classificação" is where every row starts, so selected it is underlined, not tinted: a queue of unclassified
// rows would otherwise be a column of identical filled pills, and the fill would read as a choice already made.
const UNASSIGNED_CLASSES =
  'data-[state=on]:bg-transparent data-[state=on]:underline data-[state=on]:decoration-foreground/40 data-[state=on]:underline-offset-4';

// Only the treatments legal for the category's type are offered, so an illegal pair can't be composed. The
// last pill clears the assignment, which makes the group a complete radio set: an unclassified row shows it
// selected.
export const TreatmentPills: React.FC<TreatmentPillsProps> = ({ categoryType, value, onPick, label, disabled }) => {
  const legal = treatmentsForCategoryType(categoryType);
  const common = legal.filter((t) => COMMON_TREATMENTS.has(t));
  const rare = legal.filter((t) => !COMMON_TREATMENTS.has(t));
  const valueIsRare = value !== '' && value !== 'unassigned' && !COMMON_TREATMENTS.has(value);
  // Opened by hand, or held open because the row's own treatment is one of the rare ones.
  const [moreOpen, setMoreOpen] = useState(false);
  const showRare = moreOpen || valueIsRare;

  const pill = (pick: AssignmentPick) => (
    <ToggleGroupItem key={pick} value={pick} className={cn(PILL_CLASSES, pick === 'unassigned' && UNASSIGNED_CLASSES)}>
      {pick === 'unassigned' ? BUDGET_UNASSIGNED_LABEL : translateBudgetTreatment(pick)}
    </ToggleGroupItem>
  );

  // "Mais opções" sits right after the common pills, where the rare ones open: inside the group's box but not
  // one of its items, so arrow keys move between pills and the button is its own Tab stop.
  return (
    <ToggleGroup
      type="single"
      value={value}
      // Pressing the selected pill again reports '' — that is not a choice, and must not clear the assignment.
      onValueChange={(next) => next && onPick(next as AssignmentPick)}
      aria-label={label}
      disabled={disabled}
      className="flex-wrap justify-start"
    >
      {common.map(pill)}
      {rare.length > 0 && !valueIsRare && !disabled && (
        <button
          type="button"
          aria-expanded={showRare}
          onClick={() => setMoreOpen((open) => !open)}
          className="inline-flex h-11 items-center gap-1 rounded-md px-2.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:h-9"
        >
          {showRare ? BUDGET_MORE_TREATMENTS.hide : BUDGET_MORE_TREATMENTS.show}
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform motion-reduce:transition-none', showRare && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
      )}
      {showRare && rare.map(pill)}
      {pill('unassigned')}
    </ToggleGroup>
  );
};
